import { useEffect, useMemo, useRef } from 'react';
import { type FieldPath, type UseFormReturn, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useLocationDatesValidation } from '../../../validation/location-dates';
import { buildLocations, getEffectiveDates } from '../date-validation-helpers';
import { FIELD_KEYS } from '../field-keys';
import type { Baselines, Row } from '../multi-update-types';
import { applyValidatorPathsToFormErrors } from '../multi-update-utils';

type MultiUpdateFormValues = { rows: Row[] };

export const useMultiUpdateDatesValidationAdapter = <
	TForm extends MultiUpdateFormValues,
>({
	form,
	baselines,
	publishing = false,
	skipPublishUntilValidation = false,
}: {
	form: UseFormReturn<TForm>;
	baselines: Baselines;
	publishing?: boolean;
	skipPublishUntilValidation?: boolean;
}) => {
	const { t } = useTranslation();
	const { validateLocationDates } = useLocationDatesValidation();
	const rows =
		(useWatch<TForm>({
			control: form.control,
			name: 'rows' as FieldPath<TForm>,
		}) as unknown as Row[]) ?? [];

	// Effective dates = user value if present, otherwise baseline
	const effectiveDates = useMemo(
		() => getEffectiveDates(rows, baselines, publishing),
		[rows, baselines, publishing],
	);

	// Build validator input from effective dates
	const locations = useMemo(
		() => buildLocations(effectiveDates),
		[effectiveDates],
	);

	const results = useMemo(
		() =>
			validateLocationDates(
				locations,
				effectiveDates[FIELD_KEYS.PUBLISH_UNTIL] ?? '',
				publishing,
				!publishing || skipPublishUntilValidation,
			),
		[
			locations,
			effectiveDates,
			publishing,
			skipPublishUntilValidation,
			validateLocationDates,
		],
	);

	const appliedErrorNamesRef = useRef<string[]>([]);

	// biome-ignore lint/plugin/no-use-effect: pushes validator results into RHF error state on every result change
	useEffect(() => {
		const issues = results.valid ? [] : results.issues;

		// Any issue that can't be shown on its own row must be
		// surfaced on a row that IS present, otherwise the disabled submit button has
		// no matching inline error and the user isn't told why they can't update.
		const hasPickupUntilRow = rows.some(
			(row) => row?.key === FIELD_KEYS.PICKUP_DATE_UNTIL,
		);
		const hasPublishUntilRow = rows.some(
			(row) => row?.key === FIELD_KEYS.PUBLISH_UNTIL,
		);

		const mappedIssues = issues.map((issue) => {
			// Publish-until constraint reported on the publish-until field, but the
			// user only edited pick-up-until → surface it on the pick-up-until row.
			if (issue.path === 'publishUntilDate' && !hasPublishUntilRow) {
				return { message: issue.message, path: 'locations.0.action.dateUntil' };
			}

			// Symmetric case: pick-up-until (from the unchanged baseline) is in the
			// past, but the user only edited publish-until, so there is no
			// pick-up-until row to host the "must be in the future" error. Surface a
			// steering message on the publish-until row telling the user to also add
			// and update pick-up-until, instead of silently disabling the button.
			if (
				issue.path === 'locations.0.action.dateUntil' &&
				!hasPickupUntilRow &&
				hasPublishUntilRow
			) {
				return {
					message: t(
						'general.errors.shipment.location_dates.pickup_until_past_add_field',
						'Pick-up until is in the past. Add "Pick-up until" and set it to a future date.',
					),
					path: 'publishUntilDate',
				};
			}

			return { message: issue.message, path: issue.path };
		});

		const seenPaths = new Set<string>();
		const dedupedIssues = mappedIssues.filter((issue) => {
			if (seenPaths.has(issue.path)) {
				return false;
			}
			seenPaths.add(issue.path);
			return true;
		});

		const appliedNames = applyValidatorPathsToFormErrors({
			form,
			rows,
			result: { errors: dedupedIssues },
		});

		const staleNames = appliedErrorNamesRef.current.filter(
			(name) => !appliedNames.includes(name),
		);
		if (staleNames.length > 0) {
			form.clearErrors(staleNames as FieldPath<TForm>[]);
		}
		appliedErrorNamesRef.current = appliedNames;
	}, [results, rows, form, t]);

	// The zod resolver (which drives `isValid`) can't see these cross-field,
	// baseline-aware date rules, so expose validity for the caller to gate
	// submission on — otherwise the save proceeds despite the inline error.
	return { hasDateErrors: !results.valid };
};
